import React, { useState, useEffect, useRef } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { buscarUbigeo } from '../utils/ubigeo';
import { GridOn } from '@mui/icons-material';
import axios from 'axios';

export default function UbigeoTable() {
  const [allData, setAllData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  useEffect(() => {
    const fetchData = async () => {
      const results = await buscarUbigeo('');
      setAllData(results);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const search = query.toLowerCase();
    const filteredRows = allData.filter(row => {
      const combined = `${row.departamento}, ${row.provincia}, ${row.distrito}`.toLowerCase();
      const codCombined = `${row.codigo} - ${row.departamento}, ${row.provincia}, ${row.distrito}`.toLowerCase();
      return (
        row.codigo.toLowerCase().includes(search) ||
        row.departamento.toLowerCase().includes(search) ||
        row.provincia.toLowerCase().includes(search) ||
        row.distrito.toLowerCase().includes(search) ||
        combined.includes(search) ||
        codCombined.includes(search)
      );
    });
    setFiltered(filteredRows);
    setPage(0);
  }, [query, allData]);


  const paginated = rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (inc) => {
    setPage(prev => prev + inc);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Exportar a Excel
  const handleExportExcel = () => {
    import('xlsx').then(XLSX => {
      const data = filtered.map(row => ({
        Código: row.codigo,
        Departamento: row.departamento,
        Provincia: row.provincia,
        Distrito: row.distrito
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ubigeo');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      import('file-saver').then(({ saveAs }) => {
        saveAs(blob, 'ubigeo.xlsx');
      });
    });
  };

  // Referencia para input de archivo
  const fileInputRef = useRef();

  // Simulación de importación de plantilla
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      alert('Importación de plantilla simulada');
    }
  };

  // Simulación de exportación de plantilla
  const handleExportPlantilla = () => {
    const tk = localStorage.getItem('token') || '';
    window.open(`/api/ubigeo/export?token=${tk}`, '_blank');
  };


  // Estado para modal de nuevo Ubigeo
  const [openNuevo, setOpenNuevo] = useState(false);
  const [nuevoUbigeo, setNuevoUbigeo] = useState({ codigo: '', departamento: '', provincia: '', distrito: '' });
  const [editUbigeo, setEditUbigeo] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [deleteUbigeo, setDeleteUbigeo] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  // Editar Ubigeo
  const handleEditUbigeo = (row) => {
    setEditUbigeo({ ...row });
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditUbigeo(null);
  };
  const handleChangeEdit = e => {
    setEditUbigeo({ ...editUbigeo, [e.target.name]: e.target.value });
  };
  const handleGuardarEdit = async () => {
    try {
      await axios.put(`/api/ubigeo/${editUbigeo.codigo}`, editUbigeo);
      const results = await buscarUbigeo(query);
      setAllData(results);
      handleCloseEdit();
    } catch (err) {
      alert('Error al editar ubigeo');
    }
  };

  // Eliminar Ubigeo
  const handleDeleteUbigeo = (row) => {
    setDeleteUbigeo(row);
    setOpenDelete(true);
  };
  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteUbigeo(null);
  };
  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/ubigeo/${deleteUbigeo.codigo}`);
      const results = await buscarUbigeo(query);
      setAllData(results);
      handleCloseDelete();
    } catch (err) {
      alert('Error al eliminar ubigeo');
    }
  };

  const handleNuevoUbigeo = () => setOpenNuevo(true);
  const handleCloseNuevo = () => {
    setOpenNuevo(false);
    setNuevoUbigeo({ codigo: '', departamento: '', provincia: '', distrito: '' });
  };
  const handleChangeNuevo = e => {
    setNuevoUbigeo({ ...nuevoUbigeo, [e.target.name]: e.target.value });
  };
  const handleGuardarNuevo = async () => {
    try {
      await axios.post('/api/ubigeo', nuevoUbigeo);
      const results = await buscarUbigeo(query);
      setAllData(results);
      handleCloseNuevo();
    } catch (err) {
      alert('Error al crear ubigeo');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '100%', md: '100%' }, maxWidth: '100%' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Listado de Ubigeo</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar..."
            value={query}
            onChange={e => { setQuery(e.target.value); setPage(0); }}
            sx={{ flex: 1 }}
          />
          <Button color="success" variant="contained" sx={{ minWidth: 120 }} onClick={handleExportExcel} startIcon={<GridOn />}>
            Exportar Excel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNuevoUbigeo}
            sx={{ minWidth: 140 }}
          >
            Nuevo Ubigeo
          </Button>
          <Button
            component="label"
            variant="contained"
            color="success"
            startIcon={<CloudUploadIcon />}
            sx={{ minWidth: 160 }}
          >
            Importar plantilla
            <input
              type="file"
              accept=".xlsx"
              hidden
              ref={fileInputRef}
              onChange={handleImport}
            />
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportPlantilla}
            sx={{ minWidth: 160 }}
          >
            Exportar plantilla
          </Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            sx={{ width: 100 }}
          />
        </Box>

        {/* Modal para nuevo Ubigeo */}
        <Dialog open={openNuevo} onClose={handleCloseNuevo} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#222', pb: 1 }}>Registrar Nuevo Ubigeo</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Código"
                name="codigo"
                value={nuevoUbigeo.codigo}
                onChange={handleChangeNuevo}
                fullWidth
                autoFocus
                sx={{ width: '100%' }}
              />
              <TextField
                label="Departamento"
                name="departamento"
                value={nuevoUbigeo.departamento}
                onChange={handleChangeNuevo}
                fullWidth
                sx={{ width: '100%' }}
              />
              <TextField
                label="Provincia"
                name="provincia"
                value={nuevoUbigeo.provincia}
                onChange={handleChangeNuevo}
                fullWidth
                sx={{ width: '100%' }}
              />
              <TextField
                label="Distrito"
                name="distrito"
                value={nuevoUbigeo.distrito}
                onChange={handleChangeNuevo}
                fullWidth
                sx={{ width: '100%' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNuevo} color="error" variant="contained" startIcon={<CloseIcon />}>Cancelar</Button>
            <Button onClick={handleGuardarNuevo} color="primary" variant="contained" startIcon={<SaveIcon />}>Guardar</Button>
          </DialogActions>
        </Dialog>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ pr: 8 }}>Código</TableCell>
                <TableCell align="center">Departamento</TableCell>
                <TableCell align="center">Provincia</TableCell>
                <TableCell align="center">Distrito</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell align="center" sx={{ pr: 8 }}>{row.codigo}</TableCell>
                  <TableCell align="center">{row.departamento}</TableCell>
                  <TableCell align="center">{row.provincia}</TableCell>
                  <TableCell align="center">{row.distrito}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleEditUbigeo(row)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDeleteUbigeo(row)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
                    {/* Modal para editar Ubigeo */}
                    <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
                      <DialogTitle sx={{ fontWeight: 'bold', color: '#222', pb: 1 }}>Editar Ubigeo</DialogTitle>
                      <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Código"
                            name="codigo"
                            value={editUbigeo?.codigo || ''}
                            onChange={handleChangeEdit}
                            fullWidth
                            disabled
                            sx={{ width: '100%' }}
                          />
                          <TextField
                            label="Departamento"
                            name="departamento"
                            value={editUbigeo?.departamento || ''}
                            onChange={handleChangeEdit}
                            fullWidth
                            sx={{ width: '100%' }}
                          />
                          <TextField
                            label="Provincia"
                            name="provincia"
                            value={editUbigeo?.provincia || ''}
                            onChange={handleChangeEdit}
                            fullWidth
                            sx={{ width: '100%' }}
                          />
                          <TextField
                            label="Distrito"
                            name="distrito"
                            value={editUbigeo?.distrito || ''}
                            onChange={handleChangeEdit}
                            fullWidth
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseEdit} color="error" variant="contained" startIcon={<CloseIcon />}>Cancelar</Button>
                        <Button onClick={handleGuardarEdit} color="primary" variant="contained" startIcon={<SaveIcon />}>Guardar</Button>
                      </DialogActions>
                    </Dialog>

                    {/* Modal para eliminar Ubigeo */}
                    <Dialog open={openDelete} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
                      <DialogTitle sx={{ fontWeight: 'bold', color: '#b71c1c' }}>¿Eliminar Ubigeo?</DialogTitle>
                      <DialogContent>
                        <Typography>¿Seguro que deseas eliminar el ubigeo <b>{deleteUbigeo?.codigo}</b> ({deleteUbigeo?.departamento}, {deleteUbigeo?.provincia}, {deleteUbigeo?.distrito})?</Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseDelete} color="primary" variant="contained">Cancelar</Button>
                        <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                      </DialogActions>
                    </Dialog>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
          <Button disabled={page === 0} onClick={() => handleChangePage(-1)}>Anterior</Button>
          <Button disabled={rowsPerPage === -1 ? true : (page + 1) * rowsPerPage >= filtered.length} onClick={() => handleChangePage(1)}>Siguiente</Button>
        </Box>
      </Box>
    </Box>
  );
}
